import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiPlus, FiShield, FiLoader } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { administradoresAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';
import { AdministradorCard, AdministradoresFiltros, AdministradoresPaginacao } from '../components/administradores';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: ${theme.spacing[4]};
`;

const AddButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.primary[500]}40;
  }

  &:active {
    transform: translateY(0);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${theme.spacing[6]};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.text.muted};
`;

const LoadingSpinner = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  animation: spin 1s linear infinite;
  margin-bottom: ${theme.spacing[4]};

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: ${theme.fontSizes.base};
  margin: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.text.muted};
  margin-bottom: ${theme.spacing[4]};
`;

const EmptyTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const EmptyText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const EmptyAction = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
  }
`;

const Administradores = () => {
  const navigate = useNavigate();
  const [administradores, setAdministradores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  const [filtros, setFiltros] = useState({
    search: '',
    ativo: '',
    telefone: '',
    page: 1,
    limit: 10
  });

  const carregarAdministradores = async (params) => {
    try {
      setLoading(true);
      setError(null);

      const response = await administradoresAPI.listar(params);
      if (response.data.success) {
        setAdministradores(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError('Erro ao carregar administradores');
      }
    } catch (err) {
      console.error('Erro ao carregar administradores:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarAdministradores(filtros);
  }, []);

  const handleFiltrosChange = (novosFiltros) => {
    setFiltros({
      ...novosFiltros,
      page: 1,
      limit: filtros.limit || 10
    });
  };

  // Buscar com filtros (aceita parâmetros opcionais para usar filtros específicos)
  const handleBuscar = (param = null) => {
    if (param && typeof param === 'object' && typeof param.preventDefault === 'function') {
      param.preventDefault();
      carregarAdministradores({ ...filtros, page: 1 });
      return;
    }

    const filtrosParaBusca = param || filtros;
    carregarAdministradores(filtrosParaBusca);
  };

  const handlePageChange = (page) => {
    const novosFiltros = { ...filtros, page };
    setFiltros(novosFiltros);
    carregarAdministradores(novosFiltros);
  };

  const handleItemsPerPageChange = (newLimit) => {
    const novosFiltros = { ...filtros, limit: newLimit, page: 1 };
    setFiltros(novosFiltros);
    carregarAdministradores(novosFiltros);
  };

  const handleAdicionar = () => {
    navigate('/administradores/novo');
  };

  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <LoadingSpinner>
            <FiLoader />
          </LoadingSpinner>
          <LoadingText>Carregando administradores...</LoadingText>
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <EmptyState>
          <EmptyIcon>⚠️</EmptyIcon>
          <EmptyTitle>Erro ao carregar administradores</EmptyTitle>
          <EmptyText>{error}</EmptyText>
          <EmptyAction onClick={() => carregarAdministradores(filtros)}>
            Tentar novamente
          </EmptyAction>
        </EmptyState>
      );
    }

    if (administradores.length === 0) {
      return (
        <EmptyState>
          <EmptyIcon>
            <FiShield />
          </EmptyIcon>
          <EmptyTitle>Nenhum administrador encontrado</EmptyTitle>
          <EmptyText>Não foram encontrados administradores com os filtros aplicados.</EmptyText>
          <EmptyAction onClick={handleAdicionar}>
            <FiPlus />
            Adicionar Primeiro Administrador
          </EmptyAction>
        </EmptyState>
      );
    }

    return (
      <>
        <CardsGrid>
          {administradores.map((admin) => (
            <AdministradorCard key={admin.uuid || admin.id} administrador={admin} />
          ))}
        </CardsGrid>

        {pagination.totalPages > 1 && (
          <AdministradoresPaginacao
            pagination={pagination}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            loading={loading}
          />
        )}
      </>
    );
  };

  return (
    <Layout title="Administradores">
      <Container>
        <Header>
          <AddButton onClick={handleAdicionar}>
            <FiPlus />
            Adicionar Administrador
          </AddButton>
        </Header>

        <Content>
          <AdministradoresFiltros
            filtros={filtros}
            onFiltrosChange={handleFiltrosChange}
            onBuscar={handleBuscar}
            loading={loading}
          />

          {renderContent()}
        </Content>
      </Container>
    </Layout>
  );
};

export default Administradores;


